import * as React from "react";
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/lib/shadcn/ui/dialog";
import { Button } from "@/lib/shadcn/ui/button";
import { Checkbox } from "@/lib/shadcn/ui/checkbox";
import { Input } from "@/lib/shadcn/ui/input";
import { Label } from "@/lib/shadcn/ui/label";
import { Textarea } from "@/lib/shadcn/ui/textarea";
import { OverrideReservationPayload, useOverrideReservationMutation } from "@/store/booking-management-api";

/** The reservation facts an override can correct, as the grid row holds them. */
export interface OverridableReservation {
    id: number;
    reservationCode: string;
    startDate: Date;
    endDate: Date;
    actualDaysOfStay: number;
    guestName: string;
    numberOfGuests: number;
    receivableIncome: number;
    commission: number;
    cleaningFee?: number;
    cleaningCost?: number;
    isCancelled?: boolean;
    /**
     * Set only on a cancellation the channel delivered. `cancelled_at IS NULL`
     * is the sole discriminator between a void and a real cancellation
     * (ADR-038), so the panel below cannot read `isCancelled` alone.
     */
    cancelledAt?: Date;
}

/** A non-negative decimal string, e.g. "0", "70.78". Mirrors the new-reservation form. */
const moneyRegex = /^\d+(\.\d+)?$/;

const MAX_REASON_LENGTH = 1000;

const money = z.string().trim().regex(moneyRegex, "Enter a non-negative amount");

const wholeNumber = (label: string) =>
    z.number({ error: `${label} is required` }).int(`${label} must be a whole number`).gte(0, `${label} cannot be negative`);

/**
 * manual-reservation-override/FR-025. The server re-validates all of this; the
 * form's job is to spare the operator a round-trip and to say WHICH value is
 * wrong.
 *
 * Note what is deliberately NOT rejected: zero actual nights. A compensated guest
 * who left without staying is a real correction the server accepts (spec EC-003).
 * The zero-night case that IS refused is a zero-length WINDOW — check-out on the
 * check-in day (spec EC-007) — which is why the date rule below is `<=`.
 */
const schema = z
    .object({
        receivableIncome: money,
        commission: money,
        cleaningFee: money,
        cleaningCost: money,
        actualDaysOfStay: wholeNumber("Actual nights"),
        numberOfGuests: wholeNumber("Number of guests"),
        guestName: z.string().trim().min(1, "Guest name is required").max(255, "Guest name must be 255 characters or fewer"),
        startDate: z.string().min(1, "Start date is required"),
        endDate: z.string().min(1, "End date is required"),
        changeReason: z
            .string()
            .trim()
            .min(1, "A reason is required")
            .max(MAX_REASON_LENGTH, `A reason must be ${MAX_REASON_LENGTH} characters or fewer`),
    })
    .superRefine((data, ctx) => {
        if (data.startDate && data.endDate && data.endDate <= data.startDate) {
            ctx.addIssue({
                code: "custom",
                path: ["endDate"],
                message: "End date must be after the start date",
            });
        }
    });

type FormValues = z.infer<typeof schema>;

/**
 * `<input type="date">` yields "YYYY-MM-DD" but the API binds these as Go
 * `time.Time` from a JSON body, which only parses RFC3339 (`time_format` is a
 * form/query tag and does not apply). UTC midnight keeps the Athens calendar day.
 */
function toRFC3339Date(dateOnly: string): string {
    return `${dateOnly}T00:00:00Z`;
}

/** The stored instant is UTC midnight, so read the day in UTC — not locally. */
function toDateInputValue(value: Date): string {
    return value.toISOString().slice(0, 10);
}

function toMoneyInputValue(value: number | undefined): string {
    return value === undefined || value === null ? "0" : String(value);
}

function defaultsFor(reservation: OverridableReservation): FormValues {
    return {
        receivableIncome: toMoneyInputValue(reservation.receivableIncome),
        commission: toMoneyInputValue(reservation.commission),
        cleaningFee: toMoneyInputValue(reservation.cleaningFee),
        cleaningCost: toMoneyInputValue(reservation.cleaningCost),
        actualDaysOfStay: reservation.actualDaysOfStay,
        numberOfGuests: reservation.numberOfGuests,
        guestName: reservation.guestName,
        startDate: toDateInputValue(reservation.startDate),
        endDate: toDateInputValue(reservation.endDate),
        // A reason describes a change, so it always starts empty (spec EC-020).
        changeReason: "",
    };
}

/**
 * Build the patch body: only the fields the operator actually changed, plus the
 * reason (manual-reservation-override/FR-003). Sending an unchanged field would
 * be harmless server-side but would make the version history claim the operator
 * touched something they did not.
 */
export function overridePayload(values: FormValues, original: FormValues): OverrideReservationPayload {
    const patch: OverrideReservationPayload = { changeReason: values.changeReason.trim() };
    const changed = <K extends keyof FormValues>(key: K): boolean => values[key] !== original[key];

    if (changed("receivableIncome")) patch.receivableIncome = values.receivableIncome.trim();
    if (changed("commission")) patch.commission = values.commission.trim();
    if (changed("cleaningFee")) patch.cleaningFee = values.cleaningFee.trim();
    if (changed("cleaningCost")) patch.cleaningCost = values.cleaningCost.trim();
    if (changed("actualDaysOfStay")) patch.actualDaysOfStay = values.actualDaysOfStay;
    if (changed("numberOfGuests")) patch.numberOfGuests = values.numberOfGuests;
    if (changed("guestName")) patch.guestName = values.guestName.trim();
    if (changed("startDate")) patch.startDate = toRFC3339Date(values.startDate);
    if (changed("endDate")) patch.endDate = toRFC3339Date(values.endDate);
    return patch;
}

/** The server's own words out of `{errors:[{message}]}`, or null if it said nothing usable. */
function serverErrorMessage(err: unknown): string | null {
    if (typeof err !== "object" || err === null || !("data" in err)) return null;
    const { data } = err as { data?: unknown };
    if (typeof data !== "object" || data === null || !("errors" in data)) return null;
    const { errors } = data as { errors?: unknown };
    if (!Array.isArray(errors) || errors.length === 0) return null;
    const first = errors[0] as { message?: unknown };
    return typeof first?.message === "string" ? first.message : null;
}

/**
 * `aria-invalid` alone tells a screen-reader user the value is wrong but not
 * why; the association is what gets re-read when they tab back to the field.
 */
const errorIdFor = (id: string): string => `${id}-error`;

const describedBy = (id: string, error: unknown): string | undefined => (error ? errorIdFor(id) : undefined);

const CANCEL_CONSEQUENCES =
    "Stops counting toward occupancy, revenue, the cancellation rate and filing. The booking stays in the version history. Gross income and commission set to 0 — edit them above to keep either.";

const ALREADY_CANCELLED_CONSEQUENCES = "This reservation is cancelled and counts toward nothing.";

/**
 * manual-reservation-cancellation/EC-002. A channel cancellation keeps feeding
 * the cancellation rate and the cancelled nights, so the void wording would be
 * false here — and voiding it must be asked for, not a side effect of saving
 * any other correction.
 */
const CHANNEL_CANCELLED_CONSEQUENCES =
    "The channel cancelled this reservation, so it still counts as a cancellation. Other corrections leave that alone. Tick the box to void it: it then leaves the cancellation figures of every window too, including the one it sits in now.";

/**
 * manual-reservation-cancellation/AS-007. Said BEFORE saving, or the operator
 * un-cancels and silently gets a zero-night booking.
 */
const UNCANCEL_DISCLOSURE =
    "Nights, cleaning fee and cleaning cost stay at 0 — cancelling zeroed them and restoring does not bring them back. Enter them above if this booking really happened.";

const CONSEQUENCES_ID = "ov-cancel-consequences";

interface FieldProps {
    id: string;
    label: string;
    error?: string;
    children: React.ReactNode;
}

const Field: React.FC<FieldProps> = ({ id, label, error, children }) => (
    <div className="grid gap-2">
        <Label htmlFor={id}>{label}</Label>
        {children}
        {error && (
            <p
                id={errorIdFor(id)}
                data-testid={`ov-error-${id.replace(/^ov-/, "")}`}
                className="text-destructive text-sm"
                role="alert"
            >
                {error}
            </p>
        )}
    </div>
);

/**
 * What a saved override did, as the operator armed it
 * (manual-reservation-cancellation/FR-027). Reported by the dialog rather than
 * re-derived from a later grid re-read, which can fail, arrive stale, or belong
 * to somebody else's refresh.
 *
 * Both flags carry the VOID state, not the raw `is_cancelled` (ADR-038): voiding
 * a reservation the channel had already cancelled is announced as a
 * cancellation, because that is what the operator just did to it.
 */
export interface OverrideOutcome {
    reservationCode: string;
    wasCancelled: boolean;
    isCancelled: boolean;
}

export interface OverrideReservationDialogProps {
    /** The reservation being corrected; `null` keeps the dialog closed. */
    reservation: OverridableReservation | null;
    onClose: () => void;
    /** Called after a successful override, with what that override did. */
    onOverridden: (outcome: OverrideOutcome) => void;
}

/**
 * OverrideReservationDialog (manual-reservation-override/FR-024 to FR-026) — the
 * operator corrects a reservation's own facts and says why.
 *
 * Keyed on the reservation id by the caller so opening a different row starts
 * from that row's values rather than the previous one's.
 */
const OverrideForm: React.FC<{
    reservation: OverridableReservation;
    onClose: () => void;
    onOverridden: (outcome: OverrideOutcome) => void;
}> = ({
    reservation,
    onClose,
    onOverridden,
}) => {
    const [original] = useState<FormValues>(() => defaultsFor(reservation));
    const [wasChannelCancelled] = useState<boolean>(() => (reservation.isCancelled ?? false) && !!reservation.cancelledAt);
    /**
     * The box means VOIDED, not "is cancelled" (ADR-038). A channel-cancelled
     * reservation is not voided yet, so it opens UNTICKED — otherwise any save
     * on it would clear the channel's timestamp and drop the booking out of the
     * cancellation figures of every window (FR-013).
     */
    const [wasVoided] = useState<boolean>(() => (reservation.isCancelled ?? false) && !reservation.cancelledAt);
    const [isVoided, setIsVoided] = useState<boolean>(wasVoided);
    const [serverError, setServerError] = useState<string | null>(null);
    const [overrideReservation, { isLoading }] = useOverrideReservationMutation();

    const {
        register,
        handleSubmit,
        getValues,
        setValue,
        formState: { errors },
    } = useForm<FormValues>({
        resolver: zodResolver(schema),
        defaultValues: original,
        mode: "onSubmit",
    });

    const moneyBeforeCancelling = useRef<Pick<FormValues, "receivableIncome" | "commission"> | null>(null);

    /**
     * FR-018 as amended 2026-09-04: income and commission move together. Zeroing
     * the income alone leaves the commission standing, which drives net income
     * and the payout negative and bills the owner for a booking that produced
     * nothing. Nights and cleaning are never touched here — the server zeroes
     * them on cancel, and on restore the operator restates them.
     */
    const armCancellation = (next: boolean): void => {
        setIsVoided(next);
        if (next) {
            moneyBeforeCancelling.current = {
                receivableIncome: getValues("receivableIncome"),
                commission: getValues("commission"),
            };
            setValue("receivableIncome", "0");
            setValue("commission", "0");
            return;
        }
        if (moneyBeforeCancelling.current) {
            setValue("receivableIncome", moneyBeforeCancelling.current.receivableIncome);
            setValue("commission", moneyBeforeCancelling.current.commission);
            moneyBeforeCancelling.current = null;
        }
    };

    /**
     * Ticking the box on a channel-cancelled reservation arms a VOID — saving
     * then clears the channel's timestamp (AS-008), so the button must not read
     * as an ordinary cancellation of a booking that still counts as one.
     */
    const armedAction =
        isVoided === wasVoided
            ? "save"
            : isVoided
              ? wasChannelCancelled
                  ? "void"
                  : "cancel"
              : "restore";
    const submitLabel = {
        save: "Save override",
        cancel: "Cancel reservation",
        void: "Void reservation",
        restore: "Restore reservation",
    }[armedAction];
    const isDestructive = armedAction === "cancel" || armedAction === "void";

    const consequences = wasVoided
        ? isVoided
            ? ALREADY_CANCELLED_CONSEQUENCES
            : UNCANCEL_DISCLOSURE
        : wasChannelCancelled && !isVoided
          ? CHANNEL_CANCELLED_CONSEQUENCES
          : CANCEL_CONSEQUENCES;

    const onSubmit = handleSubmit(async (values) => {
        setServerError(null);
        const patch = overridePayload(values, original);
        // Sending `isCancelled` on an already-cancelled reservation is what
        // clears `cancelled_at` (AS-008, EC-002), so it goes only when the void
        // itself was armed or taken back — never as a side effect of another
        // correction on a channel-cancelled row (FR-013).
        if (isVoided !== wasVoided) patch.isCancelled = isVoided;
        try {
            await overrideReservation({
                reservationId: reservation.id,
                patch,
            }).unwrap();
            onOverridden({ reservationCode: reservation.reservationCode, wasCancelled: wasVoided, isCancelled: isVoided });
            onClose();
        } catch (err) {
            // FR-026: the rejection is shown HERE, beside the input that has to
            // change, and the operator's entries are left untouched.
            setServerError(serverErrorMessage(err) ?? "The override was rejected by the server.");
        }
    });

    return (
        <form onSubmit={onSubmit} className="grid gap-4" noValidate aria-busy={isLoading}>
            {serverError && (
                <p
                    data-testid="ov-server-error"
                    role="alert"
                    className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                >
                    {serverError}
                </p>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
                <Field id="ov-receivable-income" label="Gross income (€)" error={errors.receivableIncome?.message}>
                    <Input
                        id="ov-receivable-income"
                        data-testid="ov-receivable-income"
                        inputMode="decimal"
                        aria-invalid={!!errors.receivableIncome}
                        aria-required="true"
                        aria-describedby={describedBy("ov-receivable-income", errors.receivableIncome)}
                        {...register("receivableIncome")}
                    />
                </Field>

                <Field id="ov-commission" label="Commission (€)" error={errors.commission?.message}>
                    <Input
                        id="ov-commission"
                        data-testid="ov-commission"
                        inputMode="decimal"
                        aria-invalid={!!errors.commission}
                        aria-required="true"
                        aria-describedby={describedBy("ov-commission", errors.commission)}
                        {...register("commission")}
                    />
                </Field>

                <Field id="ov-cleaning-fee" label="Cleaning fee (€)" error={errors.cleaningFee?.message}>
                    <Input
                        id="ov-cleaning-fee"
                        data-testid="ov-cleaning-fee"
                        inputMode="decimal"
                        aria-invalid={!!errors.cleaningFee}
                        aria-required="true"
                        aria-describedby={describedBy("ov-cleaning-fee", errors.cleaningFee)}
                        {...register("cleaningFee")}
                    />
                </Field>

                <Field id="ov-cleaning-cost" label="Cleaning cost (€)" error={errors.cleaningCost?.message}>
                    <Input
                        id="ov-cleaning-cost"
                        data-testid="ov-cleaning-cost"
                        inputMode="decimal"
                        aria-invalid={!!errors.cleaningCost}
                        aria-required="true"
                        aria-describedby={describedBy("ov-cleaning-cost", errors.cleaningCost)}
                        {...register("cleaningCost")}
                    />
                </Field>

                <Field id="ov-start-date" label="Check-in" error={errors.startDate?.message}>
                    <Input
                        id="ov-start-date"
                        data-testid="ov-start-date"
                        type="date"
                        aria-invalid={!!errors.startDate}
                        aria-required="true"
                        aria-describedby={describedBy("ov-start-date", errors.startDate)}
                        {...register("startDate")}
                    />
                </Field>

                <Field id="ov-end-date" label="Check-out" error={errors.endDate?.message}>
                    <Input
                        id="ov-end-date"
                        data-testid="ov-end-date"
                        type="date"
                        aria-invalid={!!errors.endDate}
                        aria-required="true"
                        aria-describedby={describedBy("ov-end-date", errors.endDate)}
                        {...register("endDate")}
                    />
                </Field>

                <Field id="ov-actual-days" label="Actual nights of stay" error={errors.actualDaysOfStay?.message}>
                    <Input
                        id="ov-actual-days"
                        data-testid="ov-actual-days"
                        type="number"
                        min={0}
                        aria-invalid={!!errors.actualDaysOfStay}
                        aria-required="true"
                        aria-describedby={describedBy("ov-actual-days", errors.actualDaysOfStay)}
                        {...register("actualDaysOfStay", { setValueAs: (v) => (v === "" ? undefined : Number(v)) })}
                    />
                </Field>

                <Field id="ov-num-guests" label="Number of guests" error={errors.numberOfGuests?.message}>
                    <Input
                        id="ov-num-guests"
                        data-testid="ov-num-guests"
                        type="number"
                        min={0}
                        aria-invalid={!!errors.numberOfGuests}
                        aria-required="true"
                        aria-describedby={describedBy("ov-num-guests", errors.numberOfGuests)}
                        {...register("numberOfGuests", { setValueAs: (v) => (v === "" ? undefined : Number(v)) })}
                    />
                </Field>
            </div>

            <Field id="ov-guest-name" label="Guest name" error={errors.guestName?.message}>
                <Input
                    id="ov-guest-name"
                    data-testid="ov-guest-name"
                    aria-invalid={!!errors.guestName}
                    aria-required="true"
                    aria-describedby={describedBy("ov-guest-name", errors.guestName)}
                    {...register("guestName")}
                />
            </Field>

            <Field id="ov-change-reason" label="Reason for this override" error={errors.changeReason?.message}>
                <Textarea
                    id="ov-change-reason"
                    data-testid="ov-change-reason"
                    rows={3}
                    maxLength={MAX_REASON_LENGTH}
                    placeholder="Why is this reservation being corrected?"
                    aria-invalid={!!errors.changeReason}
                    aria-required="true"
                    aria-describedby={describedBy("ov-change-reason", errors.changeReason)}
                    {...register("changeReason")}
                />
            </Field>

            <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-3">
                <div className="flex items-center gap-2">
                    <Checkbox
                        id="ov-cancel-toggle"
                        data-testid="ov-cancel-toggle"
                        checked={isVoided}
                        onCheckedChange={(checked) => armCancellation(checked === true)}
                        aria-describedby={CONSEQUENCES_ID}
                    />
                    <Label htmlFor="ov-cancel-toggle">Cancel this reservation</Label>
                </div>
                <p
                    id={CONSEQUENCES_ID}
                    data-testid={CONSEQUENCES_ID}
                    aria-live="polite"
                    className="mt-2 text-sm text-muted-foreground"
                >
                    {consequences}
                </p>
            </div>

            <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={onClose}>
                    Close
                </Button>
                <Button
                    type="submit"
                    variant={isDestructive ? "destructive" : "volt"}
                    data-testid="ov-submit"
                    disabled={isLoading}
                >
                    {isLoading ? "Saving…" : submitLabel}
                </Button>
            </DialogFooter>
        </form>
    );
};

export const OverrideReservationDialog: React.FC<OverrideReservationDialogProps> = ({
    reservation,
    onClose,
    onOverridden,
}) => (
    <Dialog
        open={reservation !== null}
        onOpenChange={(next) => {
            if (!next) onClose();
        }}
    >
        <DialogContent
            className="max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto"
            data-testid="override-reservation-dialog"
        >
            <DialogHeader>
                <DialogTitle>Override — {reservation?.reservationCode}</DialogTitle>
                <DialogDescription>
                    Correct this reservation&apos;s own facts, or cancel it. Taxes, payout and ADR are recomputed by
                    the server, and the reservation stops tracking HostHub until sync is resumed. Every field above the
                    cancellation panel is required.
                </DialogDescription>
            </DialogHeader>
            {reservation && (
                <OverrideForm
                    key={reservation.id}
                    reservation={reservation}
                    onClose={onClose}
                    onOverridden={onOverridden}
                />
            )}
        </DialogContent>
    </Dialog>
);

export default OverrideReservationDialog;
